/**
 * aiConnector — client for the AI assistant connector (ChatGPT, Claude,
 * Perplexity), plus the helpers the consent page needs to hand the caregiver
 * back to the assistant.
 *
 * Routes live on the client service and use the normal Cognito JWT, so they go
 * through the same api client as every other client route:
 *
 *   GET    /oauth/connect/{account_id}/{cognito_id}?request=<id>
 *   POST   /oauth/connect/{account_id}/{cognito_id}           { request, project_id, decision }
 *   GET    /oauth/grants/{project_id}/{account_id}/{cognito_id}
 *   DELETE /oauth/grants/{project_id}/{grant_id}/{account_id}/{cognito_id}
 *   GET    /oauth/grants/{project_id}/{grant_id}/{account_id}/{cognito_id}    what it added
 *   POST   /oauth/grants/{project_id}/{grant_id}/{account_id}/{cognito_id}    {action: "delete_items", items}
 *
 * Errors arrive as `{ success: false, error: "<code>", message }`. The api
 * client resolves them instead of throwing, so every call below returns an
 * outcome the caller can branch on without a try/catch.
 */

import { Linking, Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { apiDelete, apiGet, apiPost } from './apiClient';
import { isOwnAppUrl } from './appUrl';

// ─── Contract types ──────────────────────────────────────────────────────────

export interface ConnectApp {
  name: string;
  website_host: string | null;
  logo_domain: string | null;
}

export type PlanBlockReason = 'partner_staff' | 'no_write_permission';

export interface ConnectPlan {
  project_id: number;
  /** The account this plan belongs to. Allow must POST under THIS account. */
  account_id: string;
  name: string;
  connectable: boolean;
  reason: PlanBlockReason | null;
  reason_message: string | null;
  care_monitoring_enabled: boolean;
}

export interface ConnectRequest {
  request_expires_at: string;
  app: ConnectApp;
  account_email: string;
  scopes: string[];
  actions: string[];
  plans: ConnectPlan[];
}

export type ConnectDecision = 'allow' | 'deny';

export interface ConnectDecisionResult {
  /**
   * `https://<issuer>/oauth/authorize/continue?request=<id>&decision=<one-time token>`,
   * for allow and deny alike. The token makes this URL a credential: it goes to
   * `redirectToAssistant` and nowhere else. Never read, log, store or show it.
   */
  redirect_url: string;
  care_monitoring_turned_on: boolean;
}

// Every ended reason shows as "Disconnected"; the reason is not used for copy.
export type GrantEndedReason =
  | 'revoked'
  | 'refresh_token_reuse'
  | 'expired'
  | 'plan_access_lost'
  | 'client_removed'
  | 'app_revoked';

export interface AssistantGrant {
  id: number;
  app_name: string;
  logo_domain: string | null;
  status: 'active' | 'ended';
  created_at: string;
  last_used_at: string | null;
  ended_at: string | null;
  ended_reason: GrantEndedReason | null;
  can_revoke: boolean;
  /** Who connected it. Both are optional: a response without them shows nothing. */
  connected_by_name?: string | null;
  connected_by_cognito_id?: string | null;
}

/** What one connection brought into a plan, as the items route lists it. */
export type GrantItemType = 'note' | 'medication' | 'appointment' | 'contact' | 'reminder';

/**
 *   saved              in the plan (a note, or an approved record the app reported)
 *   pending_review     still waiting in Needs you; ending the connection removes it
 *   approved_unlinked  approved, but no record was reported, so it can't be found to remove
 */
export type GrantItemState = 'saved' | 'pending_review' | 'approved_unlinked';

export interface GrantItem {
  /** The server's handle for it ("note:12", "proposal:45", ...). Opaque to the app. */
  ref: string;
  type: GrantItemType;
  state: GrantItemState;
  label: string;
  added_at: string | null;
  /** Changed by a person since it was added; null when that can't be told. */
  edited: boolean | null;
  deletable: boolean;
}

/** Per-type counts of what can be removed (pending ones included), plus two tallies. */
export interface GrantItemCounts {
  note: number;
  medication: number;
  appointment: number;
  contact: number;
  reminder: number;
  /** Of the above, how many are still waiting for review. */
  pending_review: number;
  /** Not in the per-type counts: items that can't be removed from here. */
  not_removable: number;
}

/** Why one item was not removed. `not_found` means it was already gone. */
export type ItemRemovalError = 'not_found' | 'not_removable' | 'not_allowed' | 'failed';

export interface ItemRemovalResult {
  ref: string;
  deleted: boolean;
  error?: ItemRemovalError;
}

export interface GrantItemsPayload {
  grant: AssistantGrant;
  counts: GrantItemCounts;
  items: GrantItem[];
  /** POST only: one per ref the call dealt with. A call stops starting new ones after about 15 s. */
  results?: ItemRemovalResult[];
}

export interface RevokedGrant {
  id: number;
  status: 'ended';
  ended_at: string | null;
  ended_reason: GrantEndedReason | null;
}

// ─── Outcomes ────────────────────────────────────────────────────────────────

export interface ConnectorError {
  /** The API's error code, or `network` / `unknown` when it sent none. */
  code: string;
  status: number;
  message: string;
  /**
   * Set when the plan's Care Monitoring was switched on before the request turned
   * out to be unusable (answered or expired meanwhile). Only ever true.
   */
  careMonitoringTurnedOn?: boolean;
  /** `request_claimed` names the app the request is for, from the server's allowlist, as GET does. */
  app?: ConnectApp;
}

export type ConnectorOutcome<T> = { ok: true; data: T } | { ok: false; error: ConnectorError };

function failure(res: any): ConnectorOutcome<never> {
  const apiCode = typeof res?.error === 'string' && res.error ? res.error : '';
  const named = typeof res?.app?.name === 'string' && res.app.name.trim() !== '';
  return {
    ok: false,
    error: {
      code: apiCode || (res?.transient ? 'network' : 'unknown'),
      status: Number(res?.status) || 0,
      message: typeof res?.message === 'string' ? res.message : '',
      ...(res?.care_monitoring_turned_on === true ? { careMonitoringTurnedOn: true } : {}),
      ...(named ? { app: res.app as ConnectApp } : {}),
    },
  };
}

async function call<T>(run: () => Promise<any>, pick: (payload: any) => T | null): Promise<ConnectorOutcome<T>> {
  let res: any;
  try {
    res = await run();
  } catch {
    return { ok: false, error: { code: 'network', status: 0, message: '' } };
  }
  if (res?.success !== true) return failure(res);
  const data = pick(res.payload);
  if (data === null) return { ok: false, error: { code: 'unknown', status: Number(res?.status) || 200, message: '' } };
  return { ok: true, data };
}

const seg = (value: string | number) => encodeURIComponent(String(value));

// ─── Consent page ────────────────────────────────────────────────────────────

export function fetchConnectRequest(
  accountId: string,
  cognitoId: string,
  request: string,
): Promise<ConnectorOutcome<ConnectRequest>> {
  return call(
    () => apiGet(`/oauth/connect/${seg(accountId)}/${seg(cognitoId)}?request=${seg(request)}`),
    (payload) => (payload && payload.app && Array.isArray(payload.plans) ? (payload as ConnectRequest) : null),
  );
}

/**
 * `accountId` is the chosen plan's account for an allow (the server checks the
 * plan against the path). A deny with no plan picked has no plan account, so
 * it goes under the signed-in account.
 */
export function submitConnectDecision(
  accountId: string,
  cognitoId: string,
  body: { request: string; project_id?: number; decision: ConnectDecision },
): Promise<ConnectorOutcome<ConnectDecisionResult>> {
  return call(
    () => apiPost(`/oauth/connect/${seg(accountId)}/${seg(cognitoId)}`, body),
    (payload) => (typeof payload?.redirect_url === 'string' ? (payload as ConnectDecisionResult) : null),
  );
}

/**
 * "Switch account": the person who opened the request lets go of it, before
 * signing out, so whoever signs in next in this browser can open and answer it
 * instead of meeting `request_claimed`. Only the claimant can, and only before
 * deciding; any request error (the usual codes) means there was nothing to release.
 */
export function releaseConnectRequest(
  accountId: string,
  cognitoId: string,
  request: string,
): Promise<ConnectorOutcome<{ released: boolean }>> {
  return call(
    () => apiPost(`/oauth/connect/${seg(accountId)}/${seg(cognitoId)}`, { request, action: 'release' }),
    (payload) => (payload?.released === true ? { released: true } : null),
  );
}

// ─── Connections ─────────────────────────────────────────────────────────────

export function fetchAssistantGrants(
  projectId: number | string,
  accountId: string,
  cognitoId: string,
): Promise<ConnectorOutcome<AssistantGrant[]>> {
  return call(
    () => apiGet(`/oauth/grants/${seg(projectId)}/${seg(accountId)}/${seg(cognitoId)}`),
    (payload) => (Array.isArray(payload) ? (payload as AssistantGrant[]) : null),
  );
}

/** Idempotent: ending a grant that already ended still succeeds. */
export function revokeAssistantGrant(
  projectId: number | string,
  grantId: number,
  accountId: string,
  cognitoId: string,
): Promise<ConnectorOutcome<RevokedGrant>> {
  return call(
    () => apiDelete(`/oauth/grants/${seg(projectId)}/${seg(grantId)}/${seg(accountId)}/${seg(cognitoId)}`),
    (payload) => (payload && typeof payload === 'object' ? (payload as RevokedGrant) : null),
  );
}

const COUNT_KEYS = ['note', 'medication', 'appointment', 'contact', 'reminder', 'pending_review', 'not_removable'] as const;
const ITEM_TYPES: readonly string[] = ['note', 'medication', 'appointment', 'contact', 'reminder'];
const ITEM_STATES: readonly string[] = ['saved', 'pending_review', 'approved_unlinked'];

/** The items payload, or null when it isn't one. Counts that are missing read as 0; items that don't parse are dropped. */
function readItemsPayload(payload: any): GrantItemsPayload | null {
  if (!payload || typeof payload !== 'object' || !payload.counts || !Array.isArray(payload.items)) return null;
  const counts = Object.fromEntries(
    COUNT_KEYS.map((key) => [key, Math.max(0, Math.floor(Number(payload.counts[key]) || 0))]),
  ) as unknown as GrantItemCounts;
  const items = payload.items
    .filter((item: any) => item && typeof item.ref === 'string' && ITEM_TYPES.includes(item.type) && ITEM_STATES.includes(item.state))
    .map((item: any): GrantItem => ({
      ref: item.ref,
      type: item.type,
      state: item.state,
      label: typeof item.label === 'string' ? item.label : '',
      added_at: typeof item.added_at === 'string' ? item.added_at : null,
      edited: typeof item.edited === 'boolean' ? item.edited : null,
      deletable: item.deletable === true,
    }));
  const results = Array.isArray(payload.results)
    ? payload.results.filter((r: any) => r && typeof r.ref === 'string').map((r: any): ItemRemovalResult => ({
      ref: r.ref,
      deleted: r.deleted === true,
      ...(typeof r.error === 'string' ? { error: r.error as ItemRemovalError } : {}),
    }))
    : undefined;
  return { grant: payload.grant, counts, items, ...(results ? { results } : {}) };
}

/**
 * Everything one connection added that is still in the plan. For the person who
 * connected it and anyone who manages the plan's members, active or ended; anyone
 * else gets a 403.
 */
export function fetchGrantItems(
  projectId: number | string,
  grantId: number,
  accountId: string,
  cognitoId: string,
): Promise<ConnectorOutcome<GrantItemsPayload>> {
  return call(
    () => apiGet(`/oauth/grants/${seg(projectId)}/${seg(grantId)}/${seg(accountId)}/${seg(cognitoId)}`),
    readItemsPayload,
  );
}

/**
 * Remove a connection's items, through each kind's own delete, as the caller:
 * `'all'` or a list of refs. One call handles up to 50 items within about 15 s, and
 * answers with the same listing as the GET (what is left) plus a result per ref
 * it dealt with. Items it did not get to are still listed: ask again.
 */
export function deleteGrantItems(
  projectId: number | string,
  grantId: number,
  accountId: string,
  cognitoId: string,
  items: 'all' | string[],
): Promise<ConnectorOutcome<GrantItemsPayload>> {
  return call(
    () => apiPost(
      `/oauth/grants/${seg(projectId)}/${seg(grantId)}/${seg(accountId)}/${seg(cognitoId)}`,
      { action: 'delete_items', items },
    ),
    readItemsPayload,
  );
}

// ─── Returning to the assistant ──────────────────────────────────────────────

/**
 * The decision's `redirect_url` is the issuer's own /oauth/authorize/continue
 * with a one-time token on it; the server finishes the flow there, in the
 * browser that started it, and only then goes on to the assistant. The page
 * never sees the assistant's callback. A URL that is not plain http(s) is
 * refused whatever the API says, so a `javascript:` or `data:` URL can never be
 * navigated to.
 *
 * It is a prefix test, nothing more: the URL is a credential, so it is not
 * parsed, split or copied anywhere.
 */
export function isSafeRedirectUrl(url: unknown): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

/**
 * Same-tab navigation on the web, so the flow ends in the window the assistant
 * opened. (`Linking.openURL` would open a new tab there.)
 *
 * The URL carries the decision token, so it goes to the navigation and nowhere
 * else: not logged, stored, shown or put in an error. A navigation that throws
 * puts the URL in its message, and uncaught errors are reported, so the throw
 * is swallowed here.
 */
export function redirectToAssistant(url: string): void {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign(url);
      return;
    }
    Linking.openURL(url).catch(() => {});
  } catch {
    // Deliberately empty: see above.
  }
}

/**
 * Is this the consent page's URL? RootNavigator uses it to skip the first-run
 * tour for someone who arrived holding an authorization request.
 */
export function isConnectAuthorizeUrl(url: string | null | undefined): boolean {
  return /^https?:\/\/[^/?#]+\/connect\/authorize(?:[/?#]|$)/i.test(String(url || ''));
}

// The last consent link handed to the browser; see handOffConnectLink.
let lastHandedOffLink: string | null = null;

/**
 * An assistant's consent link has to be answered in the browser the assistant
 * opened: /oauth/authorize/continue checks a cookie only that browser holds.
 * The page itself is web-only, but a tap on its link can open the app instead:
 * on Android app.eleplan.com is a verified App Link for every path (an intent
 * filter cannot exclude one), and on iOS the site's association file covers
 * every path but /auto-login. The app does not draw the page. It passes the
 * same link to the system browser: a Custom Tab on Android, which is the
 * browser's own tab and shares its cookies, never a web view; Safari on iOS.
 *
 * Native only: on the web the page is the consent page. Only our own app's
 * consent links qualify, and each link once, so a browser that hands the link
 * straight back leaves the app where it is instead of looping.
 *
 * @returns true when `url` was a consent link and the app took it, so the
 *   caller does not treat it as anything else.
 */
export function handOffConnectLink(url: string | null | undefined): boolean {
  if (Platform.OS === 'web') return false;
  if (!url || !isConnectAuthorizeUrl(url) || !isOwnAppUrl(url)) return false;
  if (url === lastHandedOffLink) return true;
  lastHandedOffLink = url;
  // Runs on the cold-start path, ahead of the session restore: a failure to open the
  // browser must not stop the app from starting.
  try {
    const opened = Platform.OS === 'android' ? WebBrowser.openBrowserAsync(url) : Linking.openURL(url);
    Promise.resolve(opened).catch(() => {});
  } catch {
    // The link just stays unanswered; the person goes back to the assistant and starts again.
  }
  return true;
}
